import KeyRounded from "@mui/icons-material/KeyRounded";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import PhoneRounded from "@mui/icons-material/PhoneRounded";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLastNameFirst } from "../../../hooks/useNameFormat";
import { DUPLICATE_PAIR, FONT_SIZE, GRID_RADIUS, ICON_SIZE, INK } from "../../../theme/tokens";
import { formatPersonFullName } from "../../../utils/formatters";
import type { SuspectRecord } from "../../../utils/nurseMergeModels";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import MetaChip from "../../UIToolkit/Badges/MetaChip";
import { TagChip } from "../../UIToolkit/Badges/TagChip";
import { ColorDot } from "../../UIToolkit/molecules/ColorDot";
import type { MatchedField } from "./duplicatePairs";

/** Facilities named in the row before the rest fold into a "+N" chip that names them on hover. */
const MAX_VISIBLE_FACILITIES = 2;
/** Hidden once the record's own column is narrower than `RECORD_COMPACT_WIDTH` — the key glyph and its tooltip stay. */
const LOGIN_LABEL_CLASS = "duplicate-record-login-label";

type Props = {
	record: SuspectRecord;
	/** Fields that are the same on the other record — washed on both, so the match is visible in place. */
	matched: ReadonlySet<MatchedField>;
	dataTestId: string;
};

/** One side of a suggested pair: who the record is, where it works, and how to reach it. */
export const DuplicateRecordCell: FC<Props> = ({ record, matched, dataTestId }) => {
	const { t } = useTranslation();
	const lastNameFirst = useLastNameFirst();
	const name = formatPersonFullName(record, lastNameFirst);
	const loginLabel = record.hasLogin ? t("nurse.merge.login.has") : t("nurse.merge.login.none");

	return (
		<Box
			data-testid={dataTestId}
			sx={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				gap: 0.75,
				// Explicit: an inline-size container ignores its content's width, so a content-sized box would collapse to 0.
				width: "100%",
				height: "100%",
				minWidth: 0,
				// MUI sets each cell's line-height to the row height; every inline box in here would grow to it.
				lineHeight: "normal",
				containerType: "inline-size",
				[`@container (max-width: ${DUPLICATE_PAIR.RECORD_COMPACT_WIDTH}px)`]: { [`& .${LOGIN_LABEL_CLASS}`]: { display: "none" } },
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
				<Match on={matched.has("name")} tooltip={t("duplicates.record.matched")}>
					<Typography
						component="span"
						noWrap
						sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: matched.has("name") ? "primary.dark" : INK.PRIMARY, minWidth: 0 }}
					>
						{name}
					</Typography>
				</Match>
				<TagChip label={`#${record.nurseId}`} mono sx={{ flexShrink: 0 }} />
			</Box>
			<Facilities record={record} />
			<Box sx={{ display: "flex", alignItems: "center", columnGap: 1.5, minWidth: 0, fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
				<ContactValue icon={<PhoneRounded />} value={record.phoneNumber} emptyLabel={t("duplicates.record.no.phone")} matched={matched.has("phone")} />
				<ContactValue icon={<MailOutlineRounded />} value={record.email} emptyLabel={t("duplicates.record.no.email")} matched={matched.has("email")} grow />
				<InHouseTooltip title={loginLabel}>
					<Box
						component="span"
						sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0, color: record.hasLogin ? INK.SECONDARY : INK.DISABLED }}
					>
						<KeyRounded aria-hidden sx={{ fontSize: ICON_SIZE.MICRO }} />
						<span className={LOGIN_LABEL_CLASS}>{loginLabel}</span>
					</Box>
				</InHouseTooltip>
			</Box>
		</Box>
	);
};

/** The shared-value wash. Renders its children untouched when the value doesn't match. */
const Match: FC<{ on: boolean; tooltip?: string; children: ReactNode }> = ({ on, tooltip, children }) => {
	if (!on) return <>{children}</>;
	const washed = (
		<Box
			component="span"
			data-matched="true"
			sx={(theme) => ({
				display: "inline-flex",
				minWidth: 0,
				px: 0.5,
				mx: -0.5,
				borderRadius: GRID_RADIUS.BADGE,
				backgroundColor: alpha(theme.palette.primary.main, DUPLICATE_PAIR.MATCH_WASH_ALPHA),
			})}
		>
			{children}
		</Box>
	);
	return tooltip ? <InHouseTooltip title={tooltip}>{washed}</InHouseTooltip> : washed;
};

type ContactValueProps = { icon: ReactNode; value: string | null; emptyLabel: string; matched: boolean; grow?: boolean };

/** A phone or an email: the full value on hover (it ellipsizes), plus "same on both records" when it matches. */
const ContactValue: FC<ContactValueProps> = ({ icon, value, emptyLabel, matched, grow }) => {
	const { t } = useTranslation();
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: 0.5,
				minWidth: grow ? DUPLICATE_PAIR.CONTACT_MIN_WIDTH : 0,
				flexShrink: grow ? 1 : 0,
				"& svg": { fontSize: ICON_SIZE.MICRO },
			}}
		>
			<Box component="span" aria-hidden sx={{ display: "inline-flex", color: value ? INK.MUTED : INK.DISABLED }}>
				{icon}
			</Box>
			{value ? (
				<InHouseTooltip title={matched ? `${value} · ${t("duplicates.record.matched")}` : value}>
					<Box component="span" sx={{ display: "inline-flex", minWidth: 0 }}>
						<Match on={matched}>
							<Box
								component="span"
								sx={{
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									color: matched ? "primary.dark" : INK.SECONDARY,
									fontWeight: matched ? 600 : 400,
								}}
							>
								{value}
							</Box>
						</Match>
					</Box>
				</InHouseTooltip>
			) : (
				<Box component="span" sx={{ color: INK.DISABLED, whiteSpace: "nowrap" }}>
					{emptyLabel}
				</Box>
			)}
		</Box>
	);
};

const Facilities: FC<{ record: SuspectRecord }> = ({ record }) => {
	const { t } = useTranslation();
	if (record.departments.length === 0) {
		return <Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.DISABLED }}>{t("duplicates.record.facility.none")}</Typography>;
	}
	const visible = record.departments.slice(0, MAX_VISIBLE_FACILITIES);
	const hidden = record.departments.slice(MAX_VISIBLE_FACILITIES);
	const describe = (department: SuspectRecord["departments"][number]) =>
		department.active ? t("duplicates.record.facility.active", { name: department.name }) : t("duplicates.record.facility.inactive", { name: department.name });

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, overflow: "hidden" }}>
			{visible.map((department) => (
				<MetaChip
					key={department.departmentId}
					size="small"
					icon={<ColorDot size="xs" color={department.active ? "success.main" : undefined} />}
					label={department.active ? department.name : t("duplicates.record.facility.inactive.label", { name: department.name })}
					tooltip={describe(department)}
					sx={{ minWidth: 0, flexShrink: 1 }}
				/>
			))}
			{hidden.length > 0 && <MetaChip size="small" label={`+${hidden.length}`} tooltip={hidden.map(describe).join(" · ")} sx={{ flexShrink: 0 }} />}
		</Box>
	);
};
